// Team Vacation Calendar Page (VAC-8)
// Displays team vacation calendar for managers

import React, { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { RefreshCw, Calendar as CalendarIcon, Grid, List, Clock } from 'lucide-react'
import { startOfMonth, endOfMonth, format, addMonths, subMonths } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import ProtectedRoute from '@/components/ProtectedRoute'
import { CalendarMonthView } from '@/components/vacation/CalendarMonthView'
import { CalendarWeekView } from '@/components/vacation/CalendarWeekView'
import { CalendarListView } from '@/components/vacation/CalendarListView'
import { TeamCoverageGauge } from '@/components/vacation/TeamCoverageGauge'
import { useVacationCalendar } from '@/hooks/useApprovalQueue'

type ViewMode = 'month' | 'week' | 'list'

const TeamCalendarPage: React.FC = () => {
  const navigate = useNavigate()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [viewStatus, setViewStatus] = useState<'APPROVED' | 'PENDING' | 'ALL'>('APPROVED')

  // Calculate date range for API
  const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd')
  const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd')

  // Fetch calendar data
  const { data: calendarData, isLoading, error, refetch } = useVacationCalendar(
    startDate,
    endDate,
    undefined,
    viewStatus
  )

  // Navigation handlers
  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  const handleToday = () => {
    setCurrentMonth(new Date())
  }

  const handleGoToApprovals = () => {
    navigate({ to: '/vacation/approvals' })
  }

  const handleRetry = async () => {
    await refetch()
  }

  // Calculate stats
  const pendingCount = calendarData?.items.filter(item => item.status === 'PENDING').length || 0
  const totalTeam = 20 // TODO: Get from team data
  const employeesOnVacation = calendarData?.items.length || 0
  const employeeNames = calendarData?.items.map(item => item.employeeName) || []

  return (
    <ProtectedRoute>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-6">
          <nav className="text-sm text-brand-gray mb-2">
            <span>Dashboard</span>
            <span className="mx-2">›</span>
            <span>Férias</span>
            <span className="mx-2">›</span>
            <span className="text-white font-medium">
              Calendário da Equipe
            </span>
          </nav>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-display font-bold text-brand-primary">
                Calendário da Equipe
              </h1>
              <p className="text-sm text-brand-gray mt-1">
                {calendarData?.count || 0} férias no período
              </p>
            </div>
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('month')}
                className={`btn-icon ${viewMode === 'month' ? 'btn-primary' : 'btn-secondary'}`}
                title="Vista de Mês"
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`btn-icon ${viewMode === 'week' ? 'btn-primary' : 'btn-secondary'}`}
                title="Vista de Semana"
              >
                <Clock className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`btn-icon ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
                title="Vista de Lista"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Pending Approvals Banner */}
        {pendingCount > 0 && (
          <div className="bg-warning/10 border border-warning/30 rounded-monumenta p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-warning font-display font-medium">
                  Você tem {pendingCount} {pendingCount === 1 ? 'solicitação pendente' : 'solicitações pendentes'}
                </p>
              </div>
              <button
                onClick={handleGoToApprovals}
                className="btn-secondary"
              >
                Ver Fila de Aprovações
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-error/10 border border-error/30 rounded-monumenta p-6 mb-6">
            <h3 className="text-lg font-display font-semibold text-error mb-2">
              Erro ao carregar calendário
            </h3>
            <p className="text-sm text-brand-gray mb-4">
              {error.message || 'Ocorreu um erro ao carregar o calendário'}
            </p>
            <button
              onClick={handleRetry}
              className="btn-danger inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar Novamente
            </button>
          </div>
        )}

        {/* Team Coverage Gauge */}
        {!error && calendarData && employeesOnVacation > 0 && (
          <div className="mb-6">
            <TeamCoverageGauge
              employeesOnVacation={employeesOnVacation}
              totalEmployees={totalTeam}
              employeeNames={employeeNames}
            />
          </div>
        )}

        {/* Status Filter Bar */}
        <div className="card mb-6">
          <div className="flex items-center gap-2">
            <label className="text-sm font-display font-medium text-brand-gray">
              Exibir:
            </label>
            <select
              value={viewStatus}
              onChange={(e) => setViewStatus(e.target.value as 'APPROVED' | 'PENDING' | 'ALL')}
              disabled={isLoading}
              className="form-select"
            >
              <option value="APPROVED">Aprovadas</option>
              <option value="PENDING">Pendentes</option>
              <option value="ALL">Todas</option>
            </select>
          </div>
        </div>

        {/* Calendar Views */}
        <div className="card">
          {/* Month View */}
          {viewMode === 'month' && (
            <CalendarMonthView
              currentMonth={currentMonth}
              vacationEntries={calendarData?.items || []}
              onMonthChange={setCurrentMonth}
              isLoading={isLoading}
            />
          )}

          {/* Week View */}
          {viewMode === 'week' && (
            <CalendarWeekView
              currentWeek={currentMonth}
              vacationEntries={calendarData?.items || []}
              onWeekChange={setCurrentMonth}
              isLoading={isLoading}
            />
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <CalendarListView
              vacationEntries={calendarData?.items || []}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </ProtectedRoute>
  )
}

export default TeamCalendarPage
